import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Image,
  Spin,
  Tag,
  message,
  Empty,
  Modal,
  Radio,
  Table,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  TeamOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  EditOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Column, Line } from '@ant-design/charts';
import PagesWrapper from '../../PagesWrapper';
import surveysService, { ChoiceType } from '../../services/surveysService';
import styles from './SurveyStatistics.module.css';

// Color palette for different choice types (subtle, professional)
const CHOICE_TYPE_COLORS = {
  [ChoiceType.Normal]: { border: '#1890ff', bg: 'rgba(24, 144, 255, 0.04)', accent: '#1890ff' },
  [ChoiceType.Range]: { border: '#52c41a', bg: 'rgba(82, 196, 26, 0.04)', accent: '#52c41a' },
  [ChoiceType.FreeText]: { border: '#722ed1', bg: 'rgba(114, 46, 209, 0.04)', accent: '#722ed1' },
};

const CHOICE_TYPE_LABELS = {
  [ChoiceType.Normal]: 'Choice',
  [ChoiceType.Range]: 'Range',
  [ChoiceType.FreeText]: 'Free Text',
};

const SurveyStatistics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [statistics, setStatistics] = useState(null);
  
  // Modal states
  const [rangeHistogramModal, setRangeHistogramModal] = useState({ visible: false, data: null });
  const [freeTextModal, setFreeTextModal] = useState({ visible: false, data: null, sortBy: 'count' });

  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await surveysService.getSurveyStatistics(id);
      setStatistics(data);
    } catch (error) {
      message.error(t('surveys.statisticsError', 'Failed to load statistics'));
      navigate('/surveys');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const result = await surveysService.downloadSurveyExport(id, statistics?.surveyName || 'survey');
      if (result.success) {
        message.success(t('surveys.exportSuccess', 'Export downloaded successfully'));
      } else {
        message.error(t('surveys.exportError', 'Failed to export data'));
      }
    } catch (error) {
      message.error(t('surveys.exportError', 'Failed to export data'));
    } finally {
      setExporting(false);
    }
  };

  const renderSummaryCards = () => (
    <div className={styles.summaryCards}>
      <div className={styles.summaryCard}>
        <div className={styles.value}>{statistics.totalParticipants}</div>
        <div className={styles.label}>{t('surveys.totalParticipants', 'Total Participants')}</div>
      </div>
      <div className={styles.summaryCard}>
        <div className={styles.value}>{statistics.completedResponses}</div>
        <div className={styles.label}>{t('surveys.completedResponses', 'Completed')}</div>
      </div>
      <div className={styles.summaryCard}>
        <div className={styles.value}>{statistics.inProgressResponses}</div>
        <div className={styles.label}>{t('surveys.inProgress', 'In Progress')}</div>
      </div>
      <div className={styles.summaryCard}>
        <div className={styles.value}>{statistics.completionRate.toFixed(1)}%</div>
        <div className={styles.label}>{t('surveys.completionRate', 'Completion Rate')}</div>
      </div>
      <div className={styles.summaryCard}>
        <div className={styles.value}>{statistics.averageCompletionTime || '-'}</div>
        <div className={styles.label}>{t('surveys.avgTime', 'Avg. Time')}</div>
      </div>
    </div>
  );

  const renderResponseTrend = () => {
    if (!statistics.responseTrend || statistics.responseTrend.length === 0) {
      return null;
    }

    const data = statistics.responseTrend.map(item => ({
      date: new Date(item.date).toLocaleDateString(),
      count: item.count,
      cumulative: item.cumulative,
    }));

    const config = {
      data,
      xField: 'date',
      yField: 'cumulative',
      smooth: true,
      point: { size: 4 },
      color: '#1890ff',
      yAxis: {
        title: { text: t('surveys.totalResponses', 'Total Responses') },
      },
      xAxis: {
        title: { text: t('surveys.date', 'Date') },
      },
    };

    return (
      <div className={styles.chartsSection}>
        <h3 className={styles.sectionTitle}>{t('surveys.responseTrend', 'Response Trend')}</h3>
        <div className={styles.chartContainer}>
          <Line {...config} />
        </div>
      </div>
    );
  };

  const renderQuestionStatistics = () => {
    if (!statistics.questionStatistics || statistics.questionStatistics.length === 0) {
      return (
        <div className={styles.emptyState}>
          <Empty description={t('surveys.noQuestionData', 'No question data available')} />
        </div>
      );
    }

    return (
      <div className={styles.questionStatsSection}>
        <h3 className={styles.sectionTitle}>{t('surveys.questionBreakdown', 'Question Breakdown')}</h3>
        
        {statistics.questionStatistics.map((qs, index) => (
          <div key={qs.questionId} className={styles.questionCard}>
            <div className={styles.questionHeader}>
              <span className={styles.questionNumber}>Q{index + 1}</span>
              <div className={styles.questionInfo}>
                <div className={styles.questionMetaRow}>
                  {qs.questionName && (
                    <span className={styles.questionName}>{qs.questionName}</span>
                  )}
                  <div className={styles.questionBadges}>
                    {qs.isRequired && (
                      <Tag color="red" className={styles.questionBadge}>
                        {t('surveys.required', 'Required')}
                      </Tag>
                    )}
                    {qs.allowMultipleChoices && (
                      <Tag color="blue" className={styles.questionBadge}>
                        {t('surveys.multiSelect', 'Multi-select')}
                      </Tag>
                    )}
                  </div>
                </div>
                <span className={styles.questionBody}>{qs.questionBody}</span>
                {qs.questionImageUrl && (
                  <Image 
                    src={qs.questionImageUrl} 
                    alt="" 
                    className={styles.questionImage}
                  />
                )}
              </div>
            </div>

            <div className={styles.questionStats}>
              <span>{t('surveys.responses', 'Responses')}: {qs.totalResponses}</span>
              <span>{t('surveys.skipped', 'Skipped')}: {qs.skippedCount}</span>
            </div>

            {renderAllChoiceTypes(qs)}
          </div>
        ))}
      </div>
    );
  };

  // Render all choice types for a question
  const renderAllChoiceTypes = (qs) => {
    const allStats = qs.allChoiceTypeStatistics || [];
    
    // If no allChoiceTypeStatistics, fall back to legacy rendering
    if (allStats.length === 0) {
      return renderQuestionDetailsLegacy(qs);
    }

    return (
      <div className={styles.allChoiceTypes}>
        {allStats.map((typeStats) => (
          <div key={typeStats.choiceType} className={styles.choiceTypeSection}>
            {renderChoiceTypeStats(typeStats, qs)}
          </div>
        ))}
      </div>
    );
  };

  // Legacy fallback for backward compatibility
  const renderQuestionDetailsLegacy = (qs) => {
    switch (qs.primaryChoiceType) {
      case ChoiceType.Normal:
        return renderNormalChoiceDistribution(qs.choiceDistribution, ChoiceType.Normal);
      case ChoiceType.Range:
        return renderRangeStatistics({ 
          rangeAverage: qs.rangeAverage, 
          rangeMedian: qs.rangeMedian,
          rangeMin: qs.rangeMin,
          rangeMax: qs.rangeMax,
          choiceRangeMin: qs.choiceRangeMin,
          choiceRangeMax: qs.choiceRangeMax,
          rangeDistribution: qs.rangeDistribution,
          rangeMinLabel: qs.rangeMinLabel,
          rangeMaxLabel: qs.rangeMaxLabel,
          rangeChoiceText: qs.rangeChoiceText,
          choiceType: ChoiceType.Range,
          responseCount: qs.totalResponses
        });
      case ChoiceType.FreeText:
        return renderFreeTextStatistics({
          textFrequencies: qs.freeTextFrequencies,
          averageTextLength: qs.averageTextLength,
          freeTextChoiceText: qs.freeTextChoiceText,
          freeTextPlaceholder: qs.freeTextPlaceholder,
          choiceType: ChoiceType.FreeText,
          responseCount: qs.totalResponses
        });
      default:
        return null;
    }
  };

  // Render stats for a specific choice type
  const renderChoiceTypeStats = (typeStats, qs) => {
    const colors = CHOICE_TYPE_COLORS[typeStats.choiceType];
    const typeLabel = CHOICE_TYPE_LABELS[typeStats.choiceType];

    return (
      <div 
        className={styles.choiceTypeCard}
        style={{ 
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.bg 
        }}
      >
        <div className={styles.choiceTypeHeader}>
          <Tag color={colors.border} className={styles.choiceTypeTag}>
            {t(`surveys.choiceType.${typeStats.choiceType}`, typeLabel)}
          </Tag>
          <span className={styles.choiceTypeResponses}>
            {typeStats.responseCount} {t('surveys.responses', 'responses')}
          </span>
        </div>

        {typeStats.choiceType === ChoiceType.Normal && renderNormalChoiceDistribution(typeStats.choiceDistribution, typeStats.choiceType)}
        {typeStats.choiceType === ChoiceType.Range && renderRangeStatistics(typeStats)}
        {typeStats.choiceType === ChoiceType.FreeText && renderFreeTextStatistics(typeStats)}
      </div>
    );
  };

  const renderNormalChoiceDistribution = (choiceDistribution, choiceType) => {
    if (!choiceDistribution || choiceDistribution.length === 0) {
      return <Empty description={t('surveys.noChoiceData', 'No choice data available')} />;
    }

    const colors = CHOICE_TYPE_COLORS[choiceType];
    const maxCount = Math.max(...choiceDistribution.map(c => c.count));

    return (
      <div className={styles.choiceDistribution}>
        {choiceDistribution.map((choice) => (
          <div key={choice.choiceId} className={styles.choiceBar}>
            <span className={styles.choiceLabel}>
              {choice.choiceText || t('surveys.unnamedChoice', 'Choice')}
            </span>
            <div className={styles.choiceBarContainer}>
              <div
                className={styles.choiceBarFill}
                style={{ 
                  width: maxCount > 0 ? `${(choice.count / maxCount) * 100}%` : '0%',
                  backgroundColor: colors.accent
                }}
              />
            </div>
            <span className={styles.choiceCount}>
              {choice.count} ({choice.percentage.toFixed(1)}%)
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderRangeStatistics = (rangeStats) => {
    const colors = CHOICE_TYPE_COLORS[ChoiceType.Range];
    const hasData = rangeStats.rangeAverage != null;

    return (
      <div className={styles.rangeStatsWrapper}>
        {/* Range context text */}
        {(rangeStats.rangeChoiceText || rangeStats.rangeMinLabel || rangeStats.rangeMaxLabel) && (
          <div className={styles.rangeContext}>
            {rangeStats.rangeChoiceText && (
              <div className={styles.rangeChoiceText}>"{rangeStats.rangeChoiceText}"</div>
            )}
            {(rangeStats.rangeMinLabel || rangeStats.rangeMaxLabel) && (
              <div className={styles.rangeLabels}>
                <span className={styles.rangeMinLabel}>{rangeStats.rangeMinLabel || rangeStats.rangeMin}</span>
                <span className={styles.rangeSeparator}>—</span>
                <span className={styles.rangeMaxLabel}>{rangeStats.rangeMaxLabel || rangeStats.rangeMax}</span>
              </div>
            )}
          </div>
        )}

        {!hasData ? (
          <Empty description={t('surveys.noRangeData', 'No range data available')} />
        ) : (
          <>
            <div className={styles.rangeStats}>
              <div className={styles.rangeStat}>
                <div className={styles.value} style={{ color: colors.accent }}>
                  {rangeStats.rangeAverage?.toFixed(2)}
                </div>
                <div className={styles.label}>{t('surveys.average', 'Average')}</div>
              </div>
              <div className={styles.rangeStat}>
                <div className={styles.value} style={{ color: colors.accent }}>
                  {rangeStats.rangeMedian?.toFixed(2) || '-'}
                </div>
                <div className={styles.label}>{t('surveys.median', 'Median')}</div>
              </div>
              <div className={styles.rangeStat}>
                <div className={styles.value} style={{ color: colors.accent }}>
                  {rangeStats.rangeMin}
                </div>
                <div className={styles.label}>{t('surveys.min', 'Min')}</div>
              </div>
              <div className={styles.rangeStat}>
                <div className={styles.value} style={{ color: colors.accent }}>
                  {rangeStats.rangeMax}
                </div>
                <div className={styles.label}>{t('surveys.max', 'Max')}</div>
              </div>
            </div>

            {rangeStats.rangeDistribution && rangeStats.rangeDistribution.length > 0 && (
              <Button
                type="link"
                icon={<BarChartOutlined />}
                onClick={() => setRangeHistogramModal({ visible: true, data: rangeStats })}
                className={styles.viewHistogramBtn}
              >
                {t('surveys.viewHistogram', 'View Distribution Histogram')}
              </Button>
            )}
          </>
        )}
      </div>
    );
  };

  const renderFreeTextStatistics = (textStats) => {
    const frequencies = textStats.textFrequencies || [];
    const top5 = frequencies.slice(0, 5);

    return (
      <div className={styles.textResponses}>
        {/* Free text choice context */}
        {(textStats.freeTextChoiceText || textStats.freeTextPlaceholder) && (
          <div className={styles.freeTextContext}>
            {textStats.freeTextChoiceText && (
              <div className={styles.freeTextChoiceText}>"{textStats.freeTextChoiceText}"</div>
            )}
            {textStats.freeTextPlaceholder && (
              <div className={styles.freeTextPlaceholder}>
                <span>{textStats.freeTextPlaceholder}</span>
              </div>
            )}
          </div>
        )}      
        
        {top5.length > 0 ? (
          <div className={styles.topResponsesSection}>
            <div className={styles.topResponsesHeader}>
              <span className={styles.topResponsesTitle}>
                {t('surveys.topResponses', 'Top Responses by Frequency')}
              </span>
              {textStats.averageTextLength != null && (
                <div className={styles.avgTextLengthCard}>
                  <span className={styles.avgTextLengthValue}>{textStats.averageTextLength}</span>
                  <span className={styles.avgTextLengthLabel}>
                    {t('surveys.avgCharacters', 'avg. characters per response')}
                  </span>
                </div>
              )}
              {frequencies.length > 0 && (
                <Button
                  type="link"
                  size="small"
                  icon={<UnorderedListOutlined />}
                  onClick={() => setFreeTextModal({ visible: true, data: textStats, sortBy: 'count' })}
                  className={styles.viewAllBtn}
                >
                  {frequencies.length > 5 
                    ? t('surveys.viewAll', 'View all ({{count}})', { count: frequencies.length })
                    : t('surveys.viewDetails', 'Details')}
                </Button>
              )}
            </div>
            <div className={styles.topResponsesList}>
              {top5.map((item, idx) => (
                <div key={idx} className={styles.textResponseItem}>
                  <span className={styles.textResponseRank}>{idx + 1}</span>
                 
                  <div className={styles.textResponseBody}>
                    <span className={styles.textResponseText}>"{item.text}"</span>
                  </div>
                  <div className={styles.textResponseMeta}>
                      <span className={styles.textResponseCount}>{item.count}×</span>
                      <span className={styles.textResponsePercentage}>{item.percentage.toFixed(1)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Empty description={t('surveys.noTextResponses', 'No text responses yet')} />
        )}
      </div>
    );
  };

  // Range Histogram Modal
  const renderRangeHistogramModal = () => {
    const { visible, data } = rangeHistogramModal;
    if (!data) return null;

    // Use the choice-defined range (falls back to response range if not available)
    const definedMin = data.choiceRangeMin ?? data.rangeMin;
    const definedMax = data.choiceRangeMax ?? data.rangeMax;

    // Build chart data - include all values in the defined range
    const responseMap = new Map((data.rangeDistribution || []).map(d => [Number(d.value), d]));
    const chartData = [];
    
    if (definedMin != null && definedMax != null) {
      // Generate data points for the entire defined range
      for (let v = Number(definedMin); v <= Number(definedMax); v++) {
        const responseData = responseMap.get(v);
        chartData.push({
          value: v.toString(),
          count: responseData?.count || 0,
          percentage: responseData?.percentage || 0
        });
      }
    } else {
      // Fallback to just the response data
      (data.rangeDistribution || []).forEach(d => {
        chartData.push({
          value: d.value.toString(),
          count: d.count,
          percentage: d.percentage
        });
      });
    }

    const config = {
      data: chartData,
      xField: 'value',
      yField: 'count',
      color: CHOICE_TYPE_COLORS[ChoiceType.Range].accent,
      label: {
        position: 'top',
        formatter: (datum) => datum.count > 0 ? `${datum.count}` : '',
      },
      xAxis: {
        title: { text: t('surveys.value', 'Value') },
      },
      yAxis: {
        title: { text: t('surveys.responseCount', 'Response Count') },
      },
      tooltip: {
        formatter: (datum) => ({
          name: t('surveys.responses', 'Responses'),
          value: `${datum.count} (${datum.percentage.toFixed(1)}%)`
        })
      }
    };

    return (
      <Modal
        title={
          <div>
            {t('surveys.distributionHistogram', 'Distribution Histogram')}
            {data.rangeChoiceText && (
              <div className={styles.modalSubtitle}>"{data.rangeChoiceText}"</div>
            )}
            {(data.rangeMinLabel || data.rangeMaxLabel) && (
              <div className={styles.modalSubtitle}>
                {data.rangeMinLabel || definedMin} — {data.rangeMaxLabel || definedMax}
              </div>
            )}
          </div>
        }
        open={visible}
        onCancel={() => setRangeHistogramModal({ visible: false, data: null })}
        footer={null}
        width={700}
      >
        <div className={styles.histogramContainer}>
          <Column {...config} />
        </div>
        <div className={styles.histogramStats}>
          <span>{t('surveys.average', 'Average')}: <strong>{data.rangeAverage?.toFixed(2)}</strong></span>
          <span>{t('surveys.median', 'Median')}: <strong>{data.rangeMedian?.toFixed(2)}</strong></span>
          <span>{t('surveys.responseRange', 'Response Range')}: <strong>{data.rangeMin} - {data.rangeMax}</strong></span>
          {(definedMin !== data.rangeMin || definedMax !== data.rangeMax) && (
            <span>{t('surveys.definedRange', 'Defined Range')}: <strong>{definedMin} - {definedMax}</strong></span>
          )}
        </div>
      </Modal>
    );
  };

  // Free Text Modal
  const renderFreeTextModal = () => {
    const { visible, data, sortBy } = freeTextModal;
    if (!data) return null;

    const frequencies = [...(data.textFrequencies || [])];
    
    // Sort based on selection
    if (sortBy === 'length') {
      frequencies.sort((a, b) => b.textLength - a.textLength);
    } else {
      frequencies.sort((a, b) => b.count - a.count);
    }

    const columns = [
      {
        title: t('surveys.response', 'Response'),
        dataIndex: 'text',
        key: 'text',
        render: (text) => <span className={styles.modalTextResponse}>"{text}"</span>,
        ellipsis: true,
      },
      {
        title: t('surveys.count', 'Count'),
        dataIndex: 'count',
        key: 'count',
        width: 100,
        sorter: (a, b) => a.count - b.count,
      },
      {
        title: t('surveys.percentage', 'Percentage'),
        dataIndex: 'percentage',
        key: 'percentage',
        width: 120,
        render: (val) => `${val.toFixed(1)}%`,
        sorter: (a, b) => a.percentage - b.percentage,
      },
      {
        title: t('surveys.length', 'Length'),
        dataIndex: 'textLength',
        key: 'textLength',
        width: 100,
        sorter: (a, b) => a.textLength - b.textLength,
      },
    ];

    return (
      <Modal
        title={
          <div>
            {t('surveys.allTextResponses', 'All Text Responses')}
            {data.freeTextChoiceText && (
              <div className={styles.modalSubtitle}>"{data.freeTextChoiceText}"</div>
            )}
          </div>
        }
        open={visible}
        onCancel={() => setFreeTextModal({ visible: false, data: null, sortBy: 'count' })}
        footer={null}
        width={800}
      >
        <div className={styles.freeTextModalControls}>
          <span>{t('surveys.sortBy', 'Sort by')}:</span>
          <Radio.Group
            value={sortBy}
            onChange={(e) => setFreeTextModal(prev => ({ ...prev, sortBy: e.target.value }))}
            size="small"
          >
            <Radio.Button value="count">{t('surveys.frequency', 'Frequency')}</Radio.Button>
            <Radio.Button value="length">{t('surveys.textLength', 'Text Length')}</Radio.Button>
          </Radio.Group>
        </div>
        <Table
          dataSource={frequencies.map((f, idx) => ({ ...f, key: idx }))}
          columns={columns}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          size="small"
        />
        {data.averageTextLength != null && (
          <div className={styles.modalStats}>
            {t('surveys.avgTextLength', 'Average text length')}: <strong>{data.averageTextLength}</strong> {t('surveys.characters', 'characters')}
          </div>
        )}
      </Modal>
    );
  };


  if (loading) {
    return (
      <PagesWrapper>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </PagesWrapper>
    );
  }

  if (!statistics) {
    return (
      <PagesWrapper>
        <div className={styles.emptyState}>
          <Empty description={t('surveys.noStatistics', 'No statistics available')} />
        </div>
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.statisticsContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/surveys')}>
            {t('common.back', 'Back')}
          </Button>
          <h1>{statistics.surveyName} - {t('surveys.statistics', 'Statistics')}</h1>
        </div>
        <div className={styles.headerActions}>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchStatistics}
            loading={loading}
          >
            {t('common.refresh', 'Refresh')}
          </Button>
          <Button
            icon={<PlayCircleOutlined />}
            onClick={() => window.open(`/survey/${id}`, '_blank')}
          >
            {t('surveys.openSurvey', 'Open Survey')}
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => navigate(`/surveys/${id}`)}
          >
            {t('surveys.openEditor', 'Open Editor')}
          </Button>
          <Button
            icon={<TeamOutlined />}
            onClick={() => navigate(`/surveys/${id}/responses`)}
          >
            {t('surveys.viewResponses', 'View Responses')}
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
          >
            {t('surveys.exportCSV', 'Export CSV')}
          </Button>
        </div>
      </div>

      {renderSummaryCards()}
      {renderResponseTrend()}
      {renderQuestionStatistics()}
      
      {/* Modals */}
      {renderRangeHistogramModal()}
      {renderFreeTextModal()}
      </div>
    </PagesWrapper>
  );
};

export default SurveyStatistics;
